import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../firebase";
import { getUsername } from "../penpal";
import { LIMITS } from "../rooms";
import { isSlowDown } from "../pace";
import Icon from "./Icon";

// The form for commenting on a room post (or, withTitle, a simple post).
export default function RoomPostForm({
  withTitle,
  submitLabel,
  onSubmit,
}: {
  withTitle: boolean;
  submitLabel: string;
  onSubmit: (p: { username: string; title: string; body: string; cw: string; anonymous: boolean }) => Promise<void>;
}) {
  const [user] = useAuthState(auth);
  const [username, setUsername] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [cw, setCw] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) getUsername(user.uid).then(setUsername).catch(() => setUsername("unknown"));
  }, [user]);

  if (!user) return null;
  if (username === "unknown") {
    return (
      <p className="notice text-sm">
        Choose a username on <Link to="/profile">My Profile</Link> first, then you can post here.
      </p>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if ((withTitle && !title.trim()) || !body.trim()) {
      setError(withTitle ? "Please add a title and a message." : "Please write a message.");
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ username: username || "", title, body, cw, anonymous });
      setTitle("");
      setBody("");
      setCw("");
      setAnonymous(false);
    } catch (err) {
      if (isSlowDown(err)) return setBusy(false); // the wait note is showing; keep their writing
      console.error(err);
      setError(
        user.emailVerified
          ? "Sorry, that didn't post. Please try again."
          : "Please verify your email first. Use the reminder at the top of the page to resend the link."
      );
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      {withTitle && (
        <label className="field">
          <span className="field-label">Title</span>
          <input
            className="input"
            value={title}
            maxLength={LIMITS.title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. What's a grounding tool that actually works for you?"
          />
        </label>
      )}
      <label className="field">
        <span className="field-label">{withTitle ? "Your message" : "Your comment"}</span>
        <textarea
          className="input !min-h-[7rem]"
          value={body}
          maxLength={withTitle ? LIMITS.body : LIMITS.reply}
          onChange={(e) => setBody(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field-label">Content warning (optional)</span>
        <input
          className="input"
          value={cw}
          maxLength={LIMITS.cw}
          onChange={(e) => setCw(e.target.value)}
          placeholder="e.g. grief, medical stuff, family conflict"
        />
      </label>
      <label className="check items-start text-sm">
        <input type="checkbox" className="mt-1" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
        <span>
          <strong>Post anonymously.</strong> Others see "anonymous" instead of
          your username. Our moderators can still see who wrote it if it's
          reported. Anonymous comments can't receive kudos.
        </span>
      </label>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <div>
        <button type="submit" className="btn" disabled={busy}>
          <Icon name="send" size={16} /> {busy ? "Posting..." : submitLabel}
        </button>
      </div>
    </form>
  );
}
